/* CulinexOS · Data-scope context — Phase 181.85 (extended with ALL)
 * ────────────────────────────────────────────────────────────────
 * Scopes:  live | demo | simulation | all
 * `all` is administrative/diagnostic — never applied silently by any KPI.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const KEY = "cx.data_scope.v1";
const DEFAULT = "live";
const ALLOWED = new Set(["live", "demo", "simulation", "all"]);

const DataScopeContext = createContext({ scope: DEFAULT, setScope: () => {} });

export function DataScopeProvider({ children }) {
  const [scope, setScopeState] = useState(() => {
    try {
      const v = sessionStorage.getItem(KEY);
      return ALLOWED.has(v) ? v : DEFAULT;
    } catch { return DEFAULT; }
  });

  useEffect(() => {
    api.defaults.params = { ...(api.defaults.params || {}), data_scope: scope };
    try { sessionStorage.setItem(KEY, scope); } catch { /* ignore */ }
  }, [scope]);

  const setScope = useCallback((next) => {
    if (!ALLOWED.has(next)) return;
    setScopeState(next);
  }, []);

  return (
    <DataScopeContext.Provider value={{ scope, setScope }}>
      {children}
    </DataScopeContext.Provider>
  );
}

export function useDataScope() { return useContext(DataScopeContext); }

export const DATA_SCOPE_META = {
  live:       { label: "LIVE",       short: "L", color: "var(--cx-status-ok)",     hint: "Real operational data" },
  demo:       { label: "DEMO",       short: "D", color: "var(--cx-saffron)",       hint: "Synthetic training data" },
  simulation: { label: "SIMULATION", short: "S", color: "var(--cx-status-info)",   hint: "What-if scenario" },
  all:        { label: "ALL",        short: "A", color: "var(--cx-status-danger)", hint: "Diagnostic · shows LIVE + DEMO + SIMULATION combined" },
};

/* Empty-state helper shared by KPI tiles + drill sheets. */
export function scopedEmptyLabel(scope) {
  if (scope === "live")       return "No LIVE data available for this period";
  if (scope === "demo")       return "No demo data in this window";
  if (scope === "simulation") return "No simulation runs in this window";
  return "No records in this window";
}
